import React from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../variants';
import iconHtml from '../assets/icons/icon-html.png';
import iconCss from '../assets/icons/css-icon.webp';
import iconJs from '../assets/icons/icon-js.png';
import iconReact from '../assets/icons/icon-react.png';
import iconVue from '../assets/icons/vue.webp';
import iconAdobe from '../assets/icons/adobe.webp';

const ProjectDetailModal = ({ isOpen, onClose, project }) => {
    if (!isOpen || !project) return null;

    const getLanguageIcon = (lang) => {
        const icons = {
            'HTML5': iconHtml,
            'HTML': iconHtml,
            'CSS3': iconCss,
            'CSS': iconCss,
            'JavaScript': iconJs,
            'JS': iconJs,
            'React': iconReact,
            'Vue': iconVue,
            'Adobe': iconAdobe,
            'Adobe Acrobat': iconAdobe,
        };
        return icons[lang] || null;
    };

    return (
        <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 sm:p-8 max-h-screen overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <motion.div
                variants={fadeIn('up', 0.3)}
                initial="hidden"
                animate="show"
                className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden my-4 sm:my-0"
            >
                {/* Header con título y botón cerrar */}
                <div className="flex items-center justify-between px-4 sm:px-12 py-4 sm:py-8 bg-gray-50 border-b-4 border-gray-200">
                    <h2 className="text-secondBlue text-xl sm:text-4xl font-bold">
                        {project.title || 'Video en Ipad'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-4xl sm:text-6xl text-mainYellow hover:text-yellow-500 transition-colors duration-300 font-light leading-none w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center hover:bg-gray-100 rounded-full flex-shrink-0"
                    >
                        ✕
                    </button>
                </div>

                {/* Contenido principal */}
                <div className="p-4 sm:p-12 bg-white">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-12 items-start sm:items-start">
                        {/* Video/Imagen lado izquierdo */}
                        <div className="flex items-start justify-center w-full">
                            {project.videoUrl ? (
                                project.videoUrl.includes('youtube') ? (
                                    <iframe
                                        width="100%"
                                        height="auto"
                                        src={project.videoUrl}
                                        title={project.name}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="rounded-2xl shadow-2xl aspect-video"
                                    ></iframe>
                                ) : /\.(pdf)$/i.test(project.videoUrl) ? (
                                    <iframe
                                        src={project.videoUrl}
                                        title={project.name}
                                        className={`rounded-2xl shadow-2xl bg-black ${project.id > 200 ? 'max-w-lg' : 'max-w-5xl'} w-full aspect-video`}
                                    ></iframe>
                                ) : /\.(png|jpg|jpeg|gif|webp)$/i.test(project.videoUrl) ? (
                                    <img
                                        src={project.videoUrl}
                                        alt={project.name}
                                        className="w-40 h-auto rounded-2xl shadow-2xl"
                                    />
                                ) : (
                                    <video
                                        width="100%"
                                        height="auto"
                                        controls
                                        className={`rounded-2xl shadow-2xl bg-black ${project.id > 200 ? 'max-w-md' : 'max-w-4xl'}`}
                                    >
                                        <source src={project.videoUrl} type="video/mp4" />
                                        Tu navegador no soporta videos.
                                    </video>
                                )
                            ) : (
                                <div className="w-full aspect-video bg-gray-300 rounded-2xl flex items-center justify-center text-gray-500 shadow-lg">
                                    <span className="text-base sm:text-lg">Sin video o imagen disponible</span>
                                </div>
                            )}
                        </div>

                        {/* Información lado derecho */}
                        <div className="flex flex-col justify-start space-y-4 sm:space-y-8">
                            {/* Título y descripción */}
                            <div>
                                <h3 className="text-mainYellow text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">
                                    {project.name}
                                </h3>
                                <p className="text-mainBlue text-sm sm:text-xl font-semibold leading-relaxed">
                                    {project.description}
                                </p>
                            </div>

                            {/* Separador */}
                            <div className="border-b-2 border-gray-300"></div>

                            {/* Medidas */}
                            {project.medidas && (
                                <div>
                                    <p className="text-mainBlue font-bold text-sm sm:text-xl">
                                        Medidas: {project.medidas}
                                    </p>
                                </div>
                            )}

                            {/* Lenguajes */}
                            {project.languages && project.languages.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="text-mainYellow text-xl sm:text-2xl font-bold">✓</span>
                                        <p className="text-mainBlue font-bold text-sm sm:text-xl">
                                            Lenguajes:
                                        </p>
                                    </div>
                                    <div className="flex gap-4 sm:gap-6 flex-wrap">
                                        {project.languages.map((lang, idx) => {
                                            const icon = getLanguageIcon(lang);
                                            return (
                                                <div key={idx} className="flex items-center gap-2">
                                                    {icon ? (
                                                        <img
                                                            src={icon}
                                                            alt={lang}
                                                            className="h-8 sm:h-10 w-8 sm:w-10 object-contain"
                                                            title={lang}
                                                        />
                                                    ) : (
                                                        <span className="text-xl sm:text-2xl">💻</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Botón de enlace si existe */}
                            {project.url && (
                                <div className="mt-2 sm:mt-4">
                                    <a
                                        href={project.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-mainYellow text-white py-2 sm:py-4 px-4 sm:px-8 rounded-lg font-bold text-sm sm:text-lg hover:bg-yellow-500 transition-colors duration-300 shadow-lg hover:shadow-xl"
                                    >
                                        Ver Proyecto
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default ProjectDetailModal;
